import { Protect, SignedIn, useClerk, useSession, useUser } from '@clerk/clerk-react'
import { useCounts } from '@novu/react'
import { useStatsigClient } from '@statsig/react-bindings'
import {
  IconAdjustmentsCog,
  IconArrowsSplit,
  IconBolt,
  IconBorderCorners,
  IconBoxPadding,
  IconBuildingSkyscraper,
  IconChartArcs3,
  IconChecklist,
  IconFocusCentered,
  IconHelp,
  IconInbox,
  IconInfoCircle,
  IconLayoutSidebarFilled,
  IconPlayerPlay,
  IconPlus,
  IconProgressCheck,
  IconReportAnalytics,
  IconRoute,
  IconSettings,
  IconSettings2,
  IconTag,
  IconUsersGroup,
  IconX,
} from '@tabler/icons-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { clearShowAppAlertFlag, hasPendingUnsavedNavigation } from '@/utility/showAppAlertStorage'
import IconAthena from '../../assets/athena.jsx'
import PaxafeLogo from '../../assets/Logos/Poweredby/paxafeLogo.svg'
import XLogo from '../../assets/Logos/Poweredby/paxafeLogoShort.svg'
// import { decodeToken } from "../../utility/tokenUtils";
import {
  DASHBOARD_KEYS,
  getDashboardLabel,
  isLanePerformanceMenuVisible,
  resolveDashboardKeyFromParam,
} from '../../pages/InsightsDashboard/lanePerformance/lanePerformanceConfig'
import { useUserFeatureStore } from '../../stores/userFeatureStore'
import { resolveHeaderNavLabels } from '../../utility/headerNavLabels'
import { clearOrgSwitch, isOrgSwitchTransitionActive } from '../../utility/orgSwitchStorage.js'
import { RELEASE_GATE_V2_13_0 } from '../../utility/releaseStatsigGateConstants.js'
import {
  ADMIN_SETTINGS_FEATURE_GATE,
  TENANT_DASHBOARD_FEATURE_KEY,
  TENANTS_FEATURE_GATE,
} from '../../utility/tenantStatsigGateConstants.js'
import {
  checkAutomationWorkflowsViewAccess,
  checkBackendOnlyFeatureAccess,
  checkTenantFeatureAccess,
  checkUserFeatureAccess,
  clearLocalStorage,
  createClerkSupabaseClientCommandCenter,
  createRealtimeSupabaseClientCommandCenter,
  getOrganizationFeatureList,
  getUserDynamicConfig,
  getUserFeaturePermissions,
  getUserFeaturePermissionsV2,
  isTenantOwner,
  validateSessionAndRetry,
} from '../../utility/utility'
import AlertBox from '../AlertBox/AlertBox'
import Button from '../Button/Button'
import Loader from '../Loader/Loader'
import NoPermissionEmptyState from '../NoPermissionEmptyState/NoPermissionEmptyState'
import SmartScroll from '../SmartScroll/SmartScroll'
import HeaderBar from './HeaderBar'
import MenuExpand from './MenuExpand'
import UserMenu from './UserMenu'

const menuList = {
  add_new_shipment: 'Create New Shipment',
  inbox: 'Inbox',
  command_center: 'Command Center',
  athena_sandbox: 'Athena Sandbox',
  lane_performance: 'Lane Performance',
  lane_validation: 'Lane Validation',
  configuration: 'Configuration',
  automation: 'Automation',
  manage: 'Manage',
  app_config: 'Admin Settings',
  help: 'Help',
  temperature_release: 'Temperature Release',
}
let userImpersonationOrgs = false
let hasFetchedPermissions = false
let permissionsFetchedForOrgId = null
const ORG_SWITCH_PERMISSION_RETRY_DELAY_MS = 300
const ORG_SWITCH_PERMISSION_MAX_RETRIES = 15

const parseStored = (key) => {
  try {
    const raw = localStorage[key]
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const hasAnyUagUserPermission = () => {
  try {
    const permissions = JSON.parse(localStorage.getItem('userFeaturePermissions') || '{}')
    return Object.values(permissions).some((featurePermissions) =>
      Object.values(featurePermissions || {}).some(Boolean),
    )
  } catch {
    return false
  }
}

const Header = ({
  children,
  hideBreadcrumb,
  closeExpandView,
  rightComponent,
  leftComponent,
  hideTimezone = false,
  position,
  breadcrumb,
  darkThemeHeaderBar,
  dontRender,
  onDiscardUnsavedChanges,
  ..._props
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useUser()
  const { session } = useSession()
  const clerk = useClerk()
  const cc_client = createClerkSupabaseClientCommandCenter(session)
  const cc_realtimeClient = createRealtimeSupabaseClientCommandCenter(session)
  // const { getToken } = useAuth();
  const { client } = useStatsigClient()
  const uagEnhancements = client.checkGate(RELEASE_GATE_V2_13_0)
  const [permissionRetryCount, setPermissionRetryCount] = useState(0)
  const permissionRetryTimeoutRef = useRef(null)
  const [headerExpand, setHeaderExpand] = useState(false)
  const [_expandMenu, setExpandMenu] = useState(false)
  const [selected, setSelected] = useState('command_center')
  const [menu, setMenu] = useState('Command Center')
  const [menuNavigation, setMenuNavigation] = useState('')
  const [subMenuNavigation, setSubMenuNavigation] = useState('')
  const [subMenuName, setSubMenuName] = useState('')
  const [featureName, setFeatureName] = useState('')
  const [featurePermissions, setFeaturePermissions] = useState(
    () => parseStored('featurePermissions') ?? {},
  )
  const [dynamicConfig, setDynamicConfig] = useState(() => parseStored('dynamicConfig') ?? {})
  const [loading, setLoading] = useState(true)
  const [showVersionAlert, setShowVersionAlert] = useState({ version: false, releaseNotes: null })
  // const [loggedInUser, updateLoggedInUser] = useState({});
  // const [impersonateUser, setImpersonateUser] = useState(false);
  const [showAppAlert, setShowAppAlert] = useState({ show: false, path: '' })
  const [orgPoweredByPaxafe, setOrgPoweredByPaxafe] = useState(false)
  const [orgUserImpersonationEnabled, setOrgUserImpersonationEnabled] = useState(false)
  const [permissionsHydrated, setPermissionsHydrated] = useState(false)
  const environment = import.meta.env.VITE_ENVIRONMENT?.toLowerCase()
  const featureDefinitions = useUserFeatureStore((state) => state.featureDefinitions)
  const navLabelMaps = useUserFeatureStore((state) => state.navLabelMaps)

  const navLabels = useMemo(
    () =>
      resolveHeaderNavLabels({
        useDynamicNavLabels: uagEnhancements,
        navLabelMaps,
        menuList,
      }),
    [uagEnhancements, navLabelMaps],
  )

  const resolveMenuLabel = useCallback(
    (menuType) => {
      const menuTypeLabels = {
        add_new_shipment: navLabels.addNewShipment,
        inbox: navLabels.inbox,
        command_center: navLabels.commandCenter,
        temperature_release: navLabels.temperatureRelease,
        athena_sandbox: navLabels.athenaSandbox,
        qualification_sandbox: navLabels.qualificationSandbox,
        insights: navLabels.insights,
        automation: navLabels.automation,
        configuration: navLabels.configuration,
        manage: navLabels.manage,
        app_config: navLabels.adminSettings,
        'tenant-org-management': navLabels.manageOrganizations,
        help: navLabels.help,
      }

      return menuTypeLabels[menuType] || menuList[menuType] || ''
    },
    [navLabels],
  )

  // Moved navigation logic to useEffect: if user is not logged in, redirect to home
  useEffect(() => {
    if (!user) {
      navigate('/')
    }
  }, [user, navigate])

  useLayoutEffect(() => {
    const storageKey = uagEnhancements ? 'userFeaturePermissions' : 'featurePermissions'
    const hydrated = localStorage.getItem(storageKey) != null
    setPermissionsHydrated((current) => (current === hydrated ? current : hydrated))
  })

  useEffect(() => {
    releaseVersionCheck()
    releaseVersionSubscription()
  }, [location])

  async function releaseVersionCheck() {
    const releaseVersionVerified = localStorage?.releaseVersionVerified
      ? JSON.parse(localStorage?.releaseVersionVerified)
      : false
    if (releaseVersionVerified) {
      return
    }

    try {
      const releaseObj = await cc_client
        .from('release_version_tracker')
        .select('*')
        .eq('environment', environment)
        .order('release_timestamp', { ascending: false })
        .limit(1)
        .then(({ data }) => data?.[0] || null)
      const buildVersion = import.meta.env.VITE_BUILD_VERSION.split('.') || []
      const releaseVersion = releaseObj?.release_version?.split('.') || []
      if (releaseVersion[0] !== buildVersion[0] || releaseVersion[1] !== buildVersion[1]) {
        setShowVersionAlert({ version: true, releaseNotes: releaseObj?.release_notes_url || null })
      }
      localStorage.releaseVersionVerified = true
    } catch (_e) {
      // noop
    }
  }

  const channelAlreadyExists = async () => {
    const channels = cc_realtimeClient.getChannels()
    const channelExists = channels.some((channel) =>
      channel.topic.includes('release_version_tracker'),
    )
    return channelExists
  }

  async function releaseVersionSubscription() {
    const channelExists = await channelAlreadyExists()
    if (channelExists) {
      return
    }

    const topic = `topic:release_version_tracker`
    const channel = cc_realtimeClient.channel(topic, {
      config: { private: true },
    })

    channel
      .on('broadcast', { event: 'INSERT' }, (payload) => {
        const releaseObj = payload.payload
        if (releaseObj?.environment.toLowerCase() === environment) {
          const buildVersion = import.meta.env.VITE_BUILD_VERSION.split('.') || []
          const releaseVersion = releaseObj?.release_version?.split('.') || []
          if (releaseVersion[0] !== buildVersion[0] || releaseVersion[1] !== buildVersion[1]) {
            setShowVersionAlert({
              version: true,
              releaseNotes: releaseObj?.release_notes_url || null,
            })
          }
        }
      })
      .subscribe((status, error) => {
        validateSessionAndRetry({ topic, status, error }, status, session, () =>
          releaseVersionSubscription(),
        )
      })
  }

  let isAdmin = false
  if (clerk.user) {
    // Check if the user is authorized
    isAdmin = clerk.session.checkAuthorization({
      role: 'org:admin',
    })
  }
  const organizationId = window?.Clerk?.organization?.id
  const previousOrganizationIdRef = useRef(organizationId)
  const orgSwitchPermissionsReady = uagEnhancements
    ? hasAnyUagUserPermission()
    : Object.values(featurePermissions || {}).some(Boolean)
  const orgSwitchPermissionsResolved =
    orgSwitchPermissionsReady ||
    (permissionsHydrated && !loading && permissionsFetchedForOrgId === organizationId)
  const orgSwitchActive = isOrgSwitchTransitionActive() && !orgSwitchPermissionsResolved
  userImpersonationOrgs = orgUserImpersonationEnabled && isAdmin

  useEffect(() => {
    if (previousOrganizationIdRef.current !== organizationId) {
      previousOrganizationIdRef.current = organizationId
      setPermissionRetryCount(0)
      hasFetchedPermissions = false
      permissionsFetchedForOrgId = null
    }

    if (permissionsFetchedForOrgId === organizationId && hasFetchedPermissions) {
      setLoading(false)
      return
    }

    // Guard: Only fetch if we have required data and haven't fetched yet
    if (!organizationId || !user || !cc_client) {
      return
    }

    setLoading(true)
    if (uagEnhancements) {
      if (
        localStorage.getItem('userFeaturePermissions') &&
        featureDefinitions.length > 0 &&
        hasAnyUagUserPermission()
      ) {
        permissionsFetchedForOrgId = organizationId
        hasFetchedPermissions = true
        setLoading(false)
        return
      }

      getUserFeaturePermissionsV2({
        cc_client,
        callBack: () => {
          const hasAnyFeature = hasAnyUagUserPermission()
          const awaitingPermissionsForCurrentOrg = permissionsFetchedForOrgId !== organizationId
          const shouldRetryEmptyPermissions =
            (orgSwitchActive || awaitingPermissionsForCurrentOrg) &&
            !hasAnyFeature &&
            permissionRetryCount < ORG_SWITCH_PERMISSION_MAX_RETRIES

          if (shouldRetryEmptyPermissions) {
            hasFetchedPermissions = false
            permissionRetryTimeoutRef.current = setTimeout(() => {
              setPermissionRetryCount((count) => count + 1)
            }, ORG_SWITCH_PERMISSION_RETRY_DELAY_MS)
            return
          }

          permissionsFetchedForOrgId = organizationId
          hasFetchedPermissions = true
          setLoading(false)
        },
        storeData: true,
      })
      return
    }

    // Mark as fetched before making the call to prevent race conditions
    hasFetchedPermissions = true
    organizationPoweredByPaxafe(cc_client, organizationId)
    if (client.checkGate('fe-822-enhance-feature-gates-use')) {
      getUserDynamicConfig({
        cc_client,
        organizationId,
        callBack: (dynamicConfig) => {
          const _dynamicConfig = dynamicConfig?.org_config
          localStorage.dynamicConfig = JSON.stringify(_dynamicConfig)
          setDynamicConfig(_dynamicConfig)
        },
      })
    }
    getUserFeaturePermissions({
      cc_client,
      organizationId,
      user,
      callBack: (featurePermissions) => {
        const hasAnyFeature = Object.values(featurePermissions || {}).some(Boolean)
        const awaitingPermissionsForCurrentOrg = permissionsFetchedForOrgId !== organizationId
        const shouldRetryEmptyPermissions =
          (orgSwitchActive || awaitingPermissionsForCurrentOrg) &&
          !hasAnyFeature &&
          permissionRetryCount < ORG_SWITCH_PERMISSION_MAX_RETRIES

        if (shouldRetryEmptyPermissions) {
          hasFetchedPermissions = false
          permissionRetryTimeoutRef.current = setTimeout(() => {
            setPermissionRetryCount((count) => count + 1)
          }, ORG_SWITCH_PERMISSION_RETRY_DELAY_MS)
          return
        }

        permissionsFetchedForOrgId = organizationId
        hasFetchedPermissions = featurePermissions
        localStorage.featurePermissions = JSON.stringify(featurePermissions)
        setFeaturePermissions(featurePermissions)
        setLoading(false)
      },
    })

    return () => {
      if (permissionRetryTimeoutRef.current) {
        clearTimeout(permissionRetryTimeoutRef.current)
        permissionRetryTimeoutRef.current = null
      }
    }
  }, [
    organizationId,
    user,
    cc_client,
    uagEnhancements,
    orgSwitchActive,
    permissionRetryCount,
    featureDefinitions,
  ]) // Dependencies ensure it runs when data is available, but module-level flag ensures it only runs once

  useEffect(() => {
    if (
      !isOrgSwitchTransitionActive() ||
      location.pathname === '/landing-page' ||
      loading ||
      permissionsFetchedForOrgId !== organizationId
    ) {
      return
    }

    const hasFeatureAccess = uagEnhancements
      ? hasAnyUagUserPermission()
      : Object.values(featurePermissions || {}).some(Boolean)

    const permissionsFetchSettled = permissionsHydrated || hasFeatureAccess

    if (!permissionsFetchSettled) {
      return
    }

    clearOrgSwitch()
  }, [
    location.pathname,
    loading,
    organizationId,
    uagEnhancements,
    featurePermissions,
    permissionsHydrated,
  ])

  useEffect(() => {
    if (closeExpandView) {
      setHeaderExpand(false)
      updateHeaderStates()
    }
  }, [closeExpandView])

  useEffect(() => {
    loadLogos()
  }, [localStorage.shortLogo, localStorage.longLogo, headerExpand])

  useEffect(() => {
    if (selected === 'insights') {
      import('../../pages/InsightsDashboard/InsightsDashboard.jsx')
    }
  }, [selected])

  useEffect(() => {
    updateHeaderStatesOnBreadcrumbChange()
  }, [breadcrumb])

  useEffect(() => {
    updateHeaderStates()
  }, [location, breadcrumb])

  useEffect(() => {
    if (localStorage?.orgPoweredByPaxafe) {
      setOrgPoweredByPaxafe(JSON.parse(localStorage.orgPoweredByPaxafe))
    }
  }, [localStorage.orgPoweredByPaxafe])

  function updateHeaderStatesOnBreadcrumbChange() {
    setMenu(breadcrumb?.menu)
    setMenuNavigation(breadcrumb?.menuNavigation)
    setSubMenuNavigation(breadcrumb?.subMenuNavigation)
    setSubMenuName(breadcrumb?.subMenu)
    setFeatureName(breadcrumb?.featureName)
    const _selected = Object.keys(menuList).find(
      (menuType) =>
        resolveMenuLabel(menuType) === breadcrumb?.menu || menuList[menuType] === breadcrumb?.menu,
    )
    if (_selected) {
      setSelected(_selected)
    }
  }

  function updateHeaderStates() {
    const pathParts = location.pathname.split('/')
    const hashParts = location.hash.split('/')
    if (breadcrumb && Object.keys(breadcrumb).length) {
      updateHeaderStatesOnBreadcrumbChange()
      return
    }

    const isOrganizationManagementRoute =
      pathParts[1] === 'organization' && Boolean(pathParts[2]) && Boolean(pathParts[3])
    const isUserManagementRoute =
      pathParts[1] === 'user' && Boolean(pathParts[2]) && Boolean(pathParts[3])

    if (isOrganizationManagementRoute || isUserManagementRoute) {
      setSelected('')
      setMenu('')
      setSubMenuName('')
      setFeatureName('')
      setMenuNavigation('')
      setSubMenuNavigation('')
      return
    }

    if (pathParts.includes('product-profiles')) {
      if (location.state?.productName) {
        setFeatureName(location.state.productName)
      }
      setSelected('configuration')
      setSubMenuName(navLabels.productProfile)
      setMenu(navLabels.configuration)
    } else if (pathParts.includes('tags')) {
      setFeatureName('')
      setSelected('configuration')
      setSubMenuName(navLabels.tags)
      setMenu(navLabels.configuration)
    } else if (pathParts.includes('lane-profiles')) {
      if (location.state?.profileName) {
        setFeatureName(location.state.profileName)
      }
      setSelected('configuration')
      setSubMenuName(navLabels.laneProfile)
      setMenu(navLabels.configuration)
    } else if (
      pathParts.includes('shipment') &&
      pathParts.includes('create') &&
      !pathParts.includes('temperature')
    ) {
      setFeatureName('')
      setSelected('add_new_shipment')
      setSubMenuName(navLabels.addNewShipment)
      setMenu(navLabels.dashboard)
      setMenuNavigation(
        client.checkGate('shipment-list-enhancements-v1')
          ? '/shipment/commandcenter'
          : '/shipment/live',
      )
    } else if (pathParts.includes('shipment') && pathParts.includes('live')) {
      setFeatureName('')
      setSelected('command_center')
      if (client.checkGate('shipment-list-enhancements-v1')) {
        setSubMenuName(null)
      } else {
        setSubMenuName(navLabels.liveShipments)
      }
      setMenu(navLabels.commandCenter)
      setMenuNavigation(
        client.checkGate('shipment-list-enhancements-v1')
          ? '/shipment/commandcenter'
          : '/shipment/live',
      )
    } else if (pathParts.includes('shipment') && pathParts.includes('completed')) {
      setFeatureName('')
      setSelected('command_center')
      setSubMenuName(navLabels.completedShipments)
      setMenu(navLabels.commandCenter)
      setMenuNavigation('/shipment/completed')
    } else if (
      (pathParts.includes('shipment') && pathParts.includes('temperature')) ||
      (pathParts.includes('shipment') &&
        pathParts.includes('temperature') &&
        (pathParts.includes('create') ||
          pathParts.includes('summary') ||
          pathParts.includes('upload') ||
          pathParts.includes('edit')))
    ) {
      setFeatureName('')
      setSelected('temperature_release')
      setSubMenuName('')
      setMenu(navLabels.temperatureRelease)
      setMenuNavigation('/shipment/temperature')
    } else if (pathParts.includes('athena-sandbox')) {
      setFeatureName('')
      setSelected('athena_sandbox')
      setSubMenuName('')
      setMenu(navLabels.athenaSandbox)
      setMenuNavigation('/athena-sandbox')
    } else if (pathParts.includes('qualification-sandbox')) {
      setFeatureName('')
      setSelected('qualification_sandbox')
      setSubMenuName(navLabels.requestNewLane)
      setMenu('Lane Qualification')
      setMenuNavigation('/qualification-sandbox')
    } else if (
      pathParts.includes('shipment') &&
      (pathParts.includes('summary') ||
        pathParts.includes('device') ||
        pathParts.includes('timeline') ||
        pathParts.includes('liverisk') ||
        pathParts.includes('share') ||
        pathParts.includes('documents'))
    ) {
      setSelected('command_center')
    } else if (pathParts.includes('manage')) {
      setSelected('manage')
      setMenu(navLabels.manage)
      if (hashParts.includes('organization-members')) {
        setSubMenuName('Team')
      } else if (pathParts.includes('organization')) {
        setSubMenuName(navLabels.organization)
      } else if (pathParts.includes('account')) {
        setSubMenuName('Account')
      } else if (pathParts.includes('timezone')) {
        setSubMenuName('Preferred Timezone')
      } else if (pathParts.includes('impersonation')) {
        setSubMenuName('User Impersonation')
      }
      setFeatureName('')
    } else if (pathParts.includes('inbox')) {
      setSelected('inbox')
      setMenu(navLabels.inbox)
      setSubMenuName('')
      setFeatureName('')
    } else if (pathParts.includes('help')) {
      setSelected('help')
      setMenu(navLabels.help)
      setSubMenuName('')
      setFeatureName('')
    } else if (pathParts.includes('user-access-groups')) {
      if (location.state?.profileName) {
        setFeatureName(location.state.profileName)
      }
      setSelected('manage')
      setSubMenuName(navLabels.userGroup)
      setMenu(navLabels.manage)
    } else if (pathParts.includes('workflows')) {
      setSelected('automation')
      setSubMenuName(navLabels.workflows)
      setMenu(navLabels.automation)
    } else if (pathParts.includes('insights')) {
      if (pathParts.includes('command-center')) {
        setFeatureName('')
        setSelected('insights')
        setSubMenuName(navLabels.insightsCommandCenter)
        setMenu(navLabels.insights)
      } else if (pathParts.includes('temperature-release')) {
        setFeatureName('')
        setSelected('insights')
        setSubMenuName(navLabels.insightsTemperatureRelease)
        setMenu(navLabels.insights)
      } else if (pathParts.includes('lane-performance-feedback')) {
        setFeatureName('')
        setSelected('insights')
        setSubMenuName(navLabels.insightsLanePerformance)
        setMenu(navLabels.insights)
      } else if (pathParts.includes('lane-performance')) {
        const lanePerformanceIndex = pathParts.indexOf('lane-performance')
        const dashboardKeyParam = pathParts[lanePerformanceIndex + 1]
        setFeatureName(getDashboardLabel(resolveDashboardKeyFromParam(dashboardKeyParam)))
        setSelected('insights')
        setSubMenuName(navLabels.insightsLanePerformance)
        setMenu(navLabels.insights)
      } else if (pathParts.includes('sla')) {
        setFeatureName('')
        setSelected('insights')
        setSubMenuName(navLabels.sla)
        setMenu(navLabels.insights)
      }
    } else if (
      pathParts.includes('tenants') ||
      pathParts.includes('app-config') ||
      pathParts.includes('admin-settings') ||
      pathParts.includes('api-versions')
    ) {
      setSelected('app_config')
      setMenu(navLabels.adminSettings)
      if (pathParts.includes('api-versions')) {
        setSubMenuName(navLabels.apiVersions)
      } else if (pathParts.includes('admin-settings')) {
        if (pathParts.includes('organizations')) {
          setSubMenuName(navLabels.organizationManagement)
        } else if (pathParts.includes('features')) {
          setSubMenuName(navLabels.featureManagement)
        }
      } else if (pathParts.includes('tenants')) {
        setSubMenuName(navLabels.tenantOnboarding)
      }
      setFeatureName('')
    } else if (pathParts.includes('tenant') || pathParts.includes('org-management')) {
      setSelected('tenant-org-management')
      setMenu(navLabels.manageOrganizations)
      setSubMenuName('')
      setFeatureName('')
    } else {
      setFeatureName('')
    }
  }

  async function organizationPoweredByPaxafe(cc_client, organizationId) {
    const featureList = await getOrganizationFeatureList(cc_client, organizationId)
    const organizationPoweredByPaxafe = featureList[0]?.features?.powered_by_paxafe
    const userImpersonationEnabled = featureList?.[0]?.features?.user_impersonation
    if (typeof organizationPoweredByPaxafe === 'boolean') {
      localStorage.orgPoweredByPaxafe = JSON.stringify(organizationPoweredByPaxafe)
      setOrgPoweredByPaxafe(organizationPoweredByPaxafe)
    }
    if (typeof userImpersonationEnabled === 'boolean') {
      setOrgUserImpersonationEnabled(userImpersonationEnabled)
    }
  }

  function loadLogos() {
    const longLogo = document.querySelector('#wl-long-logo')
    const shortLogo = document.querySelector('#wl-short-logo')

    if (longLogo && localStorage.longLogo) {
      const img = new Image()
      img.onload = () => {
        longLogo.style.backgroundImage = `url("${localStorage.longLogo}")`
        longLogo.style.backgroundColor = 'unset'
        longLogo.style.aspectRatio = 'unset'
        longLogo.style.backgroundSize = 'contain'
        longLogo.style.backgroundRepeat = 'no-repeat'
        longLogo.style.backgroundPosition = 'left'
        // longLogo.style.width = `${img.naturalWidth}px`;
        // longLogo.style.height = `${img.naturalHeight}px`;
        longLogo.style.minHeight = '20px'
      }
      img.src = localStorage.longLogo
    }

    if (shortLogo && localStorage.shortLogo) {
      const img = new Image()
      img.onload = () => {
        shortLogo.style.backgroundImage = `url("${localStorage.shortLogo}")`
        shortLogo.style.backgroundColor = 'unset'
        shortLogo.style.aspectRatio = 'unset'
        shortLogo.style.backgroundSize = 'contain'
        shortLogo.style.backgroundRepeat = 'no-repeat'
        shortLogo.style.backgroundPosition = 'center'
        shortLogo.style.width = `${img.naturalWidth}px`
        shortLogo.style.height = `${img.naturalHeight}px`
      }
      img.src = localStorage.shortLogo
    }
  }

  const customSignOut = async () => {
    const sessionId = clerk?.session?.id
    try {
      clearLocalStorage()
      await clerk.signOut({ sessionId })
      // Redirect to sign-in page after successful sign-out
      window.location.href = '/login'
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const goToHome = (e) => {
    e.stopPropagation()
    e.preventDefault()
    hasFetchedPermissions = false
    permissionsFetchedForOrgId = null
    localStorage.viewType = 'map'
    navigate('/')
    setHeaderExpand(false)
  }

  const allTags = []

  if (
    uagEnhancements
      ? checkUserFeatureAccess('commandcenter', ['read', 'manage'])
      : featurePermissions?.commandcenter
  ) {
    const commandCenterTags = [`primary-shipment-state`, `document-upload`]
    const liveRiskTags = [`live-risk-status`, `live-risk-event`]

    allTags.push(...commandCenterTags, ...liveRiskTags)
  }

  if (
    uagEnhancements
      ? checkUserFeatureAccess('temprelease', ['read', 'manage', 'can_review'])
      : featurePermissions?.temprelease
  ) {
    const tempReleaseTags = [`temperature-release`]
    const commentsTags = [`comments`]

    allTags.push(...tempReleaseTags, ...commentsTags)
  }

  const { counts } = useCounts({
    filters: [
      {
        read: false,
        ...(allTags.length ? { tags: allTags } : {}),
      },
    ],
  })
  let unseenCount = 0
  if (counts && counts.length > 0) {
    unseenCount = counts[0]?.count || 0
  }

  const LOCATION_PATH = location.pathname.toLowerCase()
  const noShipmentCommandCenter =
    !(uagEnhancements
      ? checkUserFeatureAccess('commandcenter', ['read', 'manage'])
      : featurePermissions?.commandcenter) &&
    LOCATION_PATH.startsWith('/shipment') &&
    [
      '/shipment/commandcenter',
      '/shipment/live',
      '/shipment/completed',
      '/shipment/summary',
      '/shipment/liverisk',
      '/shipment/share',
      '/shipment/documents',
      '/shipment/timeline',
      '/shipment/device',
      '/shipment/create',
    ].some((path) => LOCATION_PATH.includes(path))

  const noTempRelease =
    !(uagEnhancements
      ? checkUserFeatureAccess('temprelease', ['read', 'manage', 'can_review'])
      : featurePermissions?.temprelease) &&
    (LOCATION_PATH.startsWith('/shipment/temperature') ||
      LOCATION_PATH.includes('/shipment/temperature') ||
      [
        '/shipment/temperature/create',
        '/upload',
        '/shipment/temperature/details',
        '/shipment/temperature/edit',
        '/shipment/temperature/summary',
      ].some((path) => LOCATION_PATH.includes(path)))

  const noTenantDashboard = uagEnhancements
    ? false
    : client.checkGate('fe-822-enhance-feature-gates-use')
      ? featurePermissions?.tenant_onboarding_steps &&
        Object.values(featurePermissions?.tenant_onboarding_steps)?.every(
          (value) => value === false,
        ) &&
        LOCATION_PATH.startsWith('/tenants')
      : !featurePermissions?.tenant_dashboard && LOCATION_PATH.startsWith('/tenants')

  const noPermissions =
    !orgSwitchActive &&
    permissionsHydrated &&
    permissionsFetchedForOrgId === organizationId &&
    !loading &&
    (noShipmentCommandCenter || noTempRelease || noTenantDashboard)
  const isSearchPopupActive = position?.includes('search-popup')
  const getMenuIconClass = (isSelected, hasSubmenu) => {
    if (!isSelected) {
      return 'text-Icon-placeholder'
    }
    return hasSubmenu && headerExpand ? 'text-Icon-strong-06' : 'text-Misc-brand'
  }

  const getSubmenuIconClass = (isSelected) => {
    return isSelected ? 'text-Misc-brand' : 'text-Icon-strong-06'
  }

  const getAdminSettingsSubmenu = () => {
    const hasTenantOnboardingStepsPermission = client.checkGate('fe-822-enhance-feature-gates-use')
      ? featurePermissions?.tenant_onboarding_steps &&
        Object.values(featurePermissions?.tenant_onboarding_steps)?.includes(true)
      : hasFetchedPermissions?.[TENANT_DASHBOARD_FEATURE_KEY] &&
        client.checkGate(TENANTS_FEATURE_GATE || '')

    const hasOrgSettingsPermission = client.checkGate('fe-822-enhance-feature-gates-use')
      ? featurePermissions?.tenant_onboarding_steps?.manage_organization
      : client.checkGate(ADMIN_SETTINGS_FEATURE_GATE)

    const hasFeatureManagementPermission = client.checkGate('fe-822-enhance-feature-gates-use')
      ? featurePermissions?.feature_flag_management
      : client.checkGate(ADMIN_SETTINGS_FEATURE_GATE)

    const hasApiVersionsPermission = client.checkGate('fe-822-enhance-feature-gates-use')
      ? featurePermissions?.api_config
      : hasFetchedPermissions?.[TENANT_DASHBOARD_FEATURE_KEY] &&
        client.checkGate(TENANTS_FEATURE_GATE || '')

    const submenu = []
    if (hasTenantOnboardingStepsPermission) {
      submenu.push({
        label: navLabels.tenantOnboarding,
        link: '/tenants',
        icon: (
          <IconChecklist
            className={`${getSubmenuIconClass(subMenuName === navLabels.tenantOnboarding)}`}
            stroke={2}
          />
        ),
      })
    }
    if (hasOrgSettingsPermission) {
      submenu.push({
        label: navLabels.organizationManagement,
        link: '/admin-settings/organizations',
        icon: (
          <IconBuildingSkyscraper
            className={`${getSubmenuIconClass(subMenuName === navLabels.organizationManagement)}`}
            stroke={2}
          />
        ),
      })
    }
    if (hasFeatureManagementPermission) {
      submenu.push({
        label: navLabels.featureManagement,
        link: '/admin-settings/features',
        icon: (
          <IconSettings
            className={`${getSubmenuIconClass(subMenuName === navLabels.featureManagement)}`}
            stroke={2}
          />
        ),
      })
    }
    if (hasApiVersionsPermission) {
      submenu.push({
        label: navLabels.apiVersions,
        link: '/api-versions',
        icon: (
          <IconArrowsSplit
            className={`${getSubmenuIconClass(subMenuName === navLabels.apiVersions)}`}
            stroke={2}
          />
        ),
      })
    }
    return submenu
  }
  const showAdminSettingsMenu = isAdmin && getAdminSettingsSubmenu()?.length > 0

  const topPoweredByPaxafe = useMemo(() => {
    return client.checkGate('fe-822-enhance-feature-gates-use')
      ? localStorage.poweredByPaxafe === 'top' && dynamicConfig?.powered_by_paxafe
      : localStorage.poweredByPaxafe === 'top' && orgPoweredByPaxafe
  }, [localStorage, dynamicConfig, orgPoweredByPaxafe])

  const bottomPoweredByPaxafe = useMemo(() => {
    return client.checkGate('fe-822-enhance-feature-gates-use')
      ? localStorage.poweredByPaxafe === 'bottom' && dynamicConfig?.powered_by_paxafe
      : localStorage.poweredByPaxafe === 'bottom' && orgPoweredByPaxafe
  }, [localStorage, dynamicConfig, orgPoweredByPaxafe])

  const haveHelpPagePermission = useMemo(() => {
    return uagEnhancements
      ? checkTenantFeatureAccess('help')
      : client.checkGate('fe-822-enhance-feature-gates-use')
        ? dynamicConfig?.help_page
        : !client.checkGate('fe-560-hide-help-page')
  }, [dynamicConfig])

  const haveUserImpersonationPermission = useMemo(() => {
    return uagEnhancements
      ? checkBackendOnlyFeatureAccess('user_impersonation')
      : client.checkGate('fe-822-enhance-feature-gates-use')
        ? featurePermissions?.user_impersonation
        : userImpersonationOrgs
  }, [featurePermissions, userImpersonationOrgs])

  const haveAdminSettingsPermission = useMemo(() => {
    return uagEnhancements
      ? false
      : client.checkGate('fe-822-enhance-feature-gates-use')
        ? dynamicConfig?.tenant_onboarding &&
          (featurePermissions?.feature_flag_management ||
            featurePermissions?.api_config ||
            (featurePermissions?.tenant_onboarding_steps &&
              Object.values(featurePermissions?.tenant_onboarding_steps)?.includes(true)))
        : showAdminSettingsMenu
  }, [dynamicConfig, featurePermissions, showAdminSettingsMenu])

  const haveOrganizationSettingsPermission = useMemo(() => {
    return uagEnhancements
      ? false
      : client.checkGate('fe-822-enhance-feature-gates-use')
        ? featurePermissions?.org_settings
        : true
  }, [featurePermissions])

  const hasCommandCenterPermission = useMemo(() => {
    return uagEnhancements
      ? checkUserFeatureAccess('commandcenter', ['read', 'manage'])
      : hasFetchedPermissions?.commandcenter
  }, [hasFetchedPermissions])

  const hasTemperatureReleasePermission = useMemo(() => {
    return uagEnhancements
      ? checkUserFeatureAccess('temprelease', ['read', 'manage', 'can_review'])
      : hasFetchedPermissions?.temprelease
  }, [hasFetchedPermissions])

  const hasInsightsTemperatureReleasePermission = useMemo(() => {
    return checkUserFeatureAccess('insights_temprelease', 'read')
  }, [hasFetchedPermissions])

  const hasInsightsLanePerformancePermission = useMemo(() => {
    return uagEnhancements
      ? checkUserFeatureAccess('insights_laneperformance', 'read')
      : isLanePerformanceMenuVisible(hasFetchedPermissions)
  }, [hasFetchedPermissions])

  const hasAutomationPermission = useMemo(() => {
    return checkAutomationWorkflowsViewAccess(uagEnhancements, {
      hasFetchedPermissions,
      featurePermissions,
    })
  }, [featurePermissions, uagEnhancements])

  const hasOldManagePermission = useMemo(() => {
    return !uagEnhancements
  }, [hasFetchedPermissions])

  const hasLaneProfilePermission = useMemo(() => {
    return uagEnhancements
      ? checkUserFeatureAccess('lane_profiles', ['read', 'manage'])
      : hasFetchedPermissions?.lane_profiles
  }, [hasFetchedPermissions])

  const hasTagPermission = useMemo(() => {
    return uagEnhancements
      ? checkUserFeatureAccess('tags', ['read', 'manage'])
      : hasFetchedPermissions?.tags
  }, [hasFetchedPermissions])

  const hasProductProfilePermission = useMemo(() => {
    return uagEnhancements
      ? checkUserFeatureAccess('product_profiles', ['read', 'manage'])
      : hasFetchedPermissions?.product_profiles
  }, [hasFetchedPermissions])

  const userFeaturePermissions = useMemo(() => {
    return (
      localStorage.getItem('userFeaturePermissions') &&
      JSON.parse(localStorage.getItem('userFeaturePermissions'))
    )
  }, [localStorage?.userFeaturePermissions])

  const hasOrganizationManagementPermission = useMemo(() => {
    return uagEnhancements && isTenantOwner()
  }, [featurePermissions])

  const hasAthenaSandboxPermission = useMemo(() => {
    return uagEnhancements
      ? checkBackendOnlyFeatureAccess('athena_sandbox')
      : client.checkGate('athena-sandbox-v1')
  }, [])

  const hasQualificationSandboxPermission = useMemo(() => {
    return uagEnhancements
      ? checkBackendOnlyFeatureAccess('qualification_sandbox')
      : client.checkGate(RELEASE_GATE_V2_13_0)
  }, [])

  return dontRender ? (
    <div
      data-testid="header-children-container-minimal"
      className={`w-full h-[calc(100vh-84px)] ${
        position?.includes('relative') ? 'relative' : 'overflow-y-auto'
      }`}
    >
      {children}
    </div>
  ) : (
    <div
      data-testid="header-container"
      className={`relative flex items-start h-screen w-screen ${
        position?.includes('relative') ? 'bg-transparent' : 'bg-BG-Default-neutral-00'
      }`}
    >
      <SignedIn>
        {showAppAlert.show && (
          <AlertBox
            title="You have unsaved changes."
            message="Are you sure you want to leave this page? Your changes will be lost."
            confirmBtnText="Confirm"
            confirmBtnType="primary"
            cancelBtnType="secondary-v3"
            onClose={() => {
              setShowAppAlert(false)
            }}
            onConfirm={() => {
              if (onDiscardUnsavedChanges && typeof onDiscardUnsavedChanges === 'function') {
                onDiscardUnsavedChanges()
              }
              setShowAppAlert((prev) => ({ ...prev, show: false, path: '' }))
              clearShowAppAlertFlag()
              if (showAppAlert.path === location.pathname) {
                window.location.reload()
              } else {
                navigate({ pathname: showAppAlert.path, search: '', replace: true })
              }
            }}
            data-testid="header-system-navigate-alert"
          />
        )}
        <div
          data-testid="header-sidebar"
          className={`relative flex shrink-0 flex-col h-full w-[70px] border-r border-Border-Default-neutral-01 bg-BG-Default-white ${
            headerExpand ? 'z-[50]' : 'z-[30]'
          }`}
        >
          <div
            className={`flex flex-col h-full min-h-screen py-4 ${
              headerExpand
                ? ' w-[255px] bg-BG-Default-white border-r border-Border-Default-neutral-01 relative z-50'
                : 'items-center'
            }`}
          >
            <div className="shrink-0 px-3 w-full flex flex-col">
              {headerExpand ? (
                <div className="flex items-center justify-between">
                  <div
                    id="wl-long-logo"
                    className="h-[18px] min-w-[150px] max-h-[30px] mt-[6px] cursor-pointer aspect-[12/3_auto] max-w-[85%]"
                    onClick={goToHome}
                  />
                  <IconLayoutSidebarFilled
                    className="text-Icon-strong-06 border-Icon-strong-06 cursor-pointer"
                    stroke={2}
                    onClick={() => {
                      setHeaderExpand(false)
                      updateHeaderStates()
                    }}
                  />
                </div>
              ) : (
                <div
                  data-testid="header-collapsed-logo-container"
                  className="w-full flex justify-center"
                >
                  <div
                    id="wl-short-logo"
                    className="h-[20px] max-h-[30px] mt-[6px] cursor-pointer aspect-[1/1_auto]"
                    onClick={goToHome}
                  />
                </div>
              )}
              {topPoweredByPaxafe && headerExpand ? (
                <div
                  data-testid="header-powered-by-paxafe-top-expanded"
                  className="flex items-center mt-2 gap-1"
                >
                  <span className="text-Text-small-04 text-[10px] ml-2.5">Powered by</span>
                  {localStorage.poweredByPaxafeSize === 'long' ? (
                    <img src={PaxafeLogo} alt="Paxafe Logo" className="h-[8px] mb-1 inline-block" />
                  ) : (
                    <img src={XLogo} alt="Paxafe Logo" className="h-[8px] mb-1 inline-block" />
                  )}
                </div>
              ) : topPoweredByPaxafe && !headerExpand ? (
                <div
                  data-testid="header-powered-by-paxafe-top-collapsed"
                  className="flex items-center mt-2 gap-1"
                >
                  <span className="text-Text-small-04 text-[6px]">Powered by</span>
                  <img src={XLogo} alt="Paxafe Logo" className="h-[8px] inline-block" />
                </div>
              ) : null}
              <div
                data-testid="header-divider-logo"
                className="h-px bg-Border-Default-neutral-01 w-full mt-2"
              ></div>
            </div>
            <div
              data-testid="header-menu-container"
              className="w-full flex-grow flex flex-col gap-2 mt-[40px] items-center overflow-y-hidden"
            >
              <SmartScroll className="w-full">
                <div className="px-3 flex flex-col gap-1">
                  {hasCommandCenterPermission && (
                    <MenuExpand
                      data-testid="add-new-shipment-menu"
                      icon={
                        <IconPlus
                          className={`
                  ${selected === 'add_new_shipment' ? 'text-white' : 'text-white'}
                   h-[22px] w-[22px]`}
                          stroke={2}
                        />
                      }
                      menu={navLabels.addNewShipment}
                      headerExpand={headerExpand}
                      setHeaderExpand={(flag) => setHeaderExpand(flag)}
                      showTooltip={true}
                      type="add_new_shipment"
                      selected={selected}
                      setSelected={(menu) => setSelected(menu)}
                      selectedTextClass="text-white"
                      setSubmenu={(flag) => setExpandMenu(flag)}
                      setSubMenuName={(name, menu) => {
                        setSubMenuName(name)
                        setMenu(resolveMenuLabel(menu))
                        setMenuNavigation(
                          client.checkGate('shipment-list-enhancements-v1')
                            ? '/shipment/commandcenter'
                            : '/shipment/live',
                        )
                      }}
                      theme="dark"
                      permissions={['org:manage:shipments']}
                      accessPermissions={checkUserFeatureAccess('commandcenter', 'manage')}
                      link="/shipment/create"
                      onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                    />
                  )}
                  <MenuExpand
                    data-testid="inbox-menu"
                    icon={
                      <div className="flex items-center justify-center relative">
                        {unseenCount > 0 && (
                          <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-Icon-critical" />
                        )}
                        <IconInbox
                          className={`${getMenuIconClass(selected === 'inbox', false)}`}
                          stroke={2}
                        />
                      </div>
                    }
                    menu={`Inbox${unseenCount > 0 ? ` (${unseenCount} unread)` : ''}`}
                    headerExpand={headerExpand}
                    setHeaderExpand={(flag) => {
                      setHeaderExpand(flag)
                    }}
                    showTooltip={true}
                    type="inbox"
                    selected={selected}
                    setSelected={(menu) => setSelected(menu)}
                    setSubmenu={(flag) => setExpandMenu(flag)}
                    setSubMenuName={(name, menu) => {
                      setSubMenuName(name)
                      setMenu(resolveMenuLabel(menu))
                    }}
                    link="/inbox"
                    onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                  />
                  <div
                    data-testid="header-divider-inbox"
                    className="h-px bg-Border-Default-neutral-01 w-full my-2"
                  ></div>
                  {hasCommandCenterPermission && (
                    <MenuExpand
                      data-testid="command-center-menu"
                      icon={
                        <IconFocusCentered
                          className={`${getMenuIconClass(selected === 'command_center')}`}
                          stroke={2}
                        />
                      }
                      menu={navLabels.commandCenter}
                      permissions={['org:shipments:read', 'org:manage:shipments']}
                      accessPermissions={checkUserFeatureAccess('commandcenter', [
                        'read',
                        'manage',
                      ])}
                      headerExpand={headerExpand}
                      setHeaderExpand={(flag) => setHeaderExpand(flag)}
                      showTooltip={true}
                      type="command_center"
                      selected={selected}
                      setSelected={(menu) => setSelected(menu)}
                      setSubmenu={(flag) => setExpandMenu(flag)}
                      subMenuName={subMenuName}
                      setSubMenuName={(name, menu) => {
                        setSubMenuName(name)
                        setMenu(resolveMenuLabel(menu))
                        setMenuNavigation('/shipment/live')
                      }}
                      link={
                        client.checkGate('shipment-list-enhancements-v1')
                          ? '/shipment/commandcenter'
                          : null
                      }
                      onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                    />
                  )}
                  {hasTemperatureReleasePermission && (
                    <MenuExpand
                      data-testid="temperature-release-menu"
                      icon={
                        <IconProgressCheck
                          className={`${getMenuIconClass(
                            selected === 'temperature_release',
                            false,
                          )}`}
                          stroke={2}
                        />
                      }
                      menu={navLabels.temperatureRelease}
                      headerExpand={headerExpand}
                      setHeaderExpand={(flag) => setHeaderExpand(flag)}
                      showTooltip={true}
                      type="temperature_release"
                      selected={selected}
                      setSelected={(menu) => setSelected(menu)}
                      setSubmenu={(flag) => setExpandMenu(flag)}
                      setSubMenuName={(name, menu) => {
                        setSubMenuName(name)
                        menu && setMenu(resolveMenuLabel(menu))
                        setMenuNavigation('/shipment/temperature')
                      }}
                      link="/shipment/temperature"
                      onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                    />
                  )}
                  {hasQualificationSandboxPermission && (
                    <MenuExpand
                      data-testid="qualification-sandbox-menu"
                      icon={
                        <IconBorderCorners
                          className={`${getMenuIconClass(
                            selected === 'qualification_sandbox',
                            false,
                          )}`}
                          stroke={2}
                        />
                      }
                      menu={navLabels.qualificationSandbox}
                      headerExpand={headerExpand}
                      setHeaderExpand={(flag) => setHeaderExpand(flag)}
                      showTooltip={true}
                      type="qualification_sandbox"
                      selected={selected}
                      setSelected={(menu) => setSelected(menu)}
                      setSubmenu={(flag) => setExpandMenu(flag)}
                      setSubMenuName={(name, menu) => {
                        setSubMenuName(name)
                        menu && setMenu(resolveMenuLabel(menu))
                        setMenuNavigation('/qualification-sandbox')
                      }}
                      link="/qualification-sandbox"
                      onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                    />
                  )}
                  {hasAthenaSandboxPermission && (
                    <MenuExpand
                      data-testid="athena-sandbox-menu"
                      icon={
                        <IconAthena
                          className={`${getMenuIconClass(selected === 'athena_sandbox', false)}`}
                          stroke={2}
                        />
                      }
                      menu={navLabels.athenaSandbox}
                      headerExpand={headerExpand}
                      setHeaderExpand={(flag) => setHeaderExpand(flag)}
                      showTooltip={true}
                      type="athena_sandbox"
                      selected={selected}
                      setSelected={(menu) => setSelected(menu)}
                      setSubmenu={(flag) => setExpandMenu(flag)}
                      setSubMenuName={(name, menu) => {
                        setSubMenuName(name)
                        menu && setMenu(resolveMenuLabel(menu))
                        setMenuNavigation('/athena-sandbox')
                      }}
                      link="/athena-sandbox"
                      onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                    />
                  )}
                  <Protect
                    condition={(has) =>
                      uagEnhancements
                        ? hasInsightsTemperatureReleasePermission ||
                          hasInsightsLanePerformancePermission ||
                          hasAutomationPermission ||
                          hasProductProfilePermission ||
                          hasLaneProfilePermission ||
                          hasTagPermission ||
                          hasOldManagePermission
                        : [
                            'org:product_profiles:manage',
                            'org:product_profiles:read',
                            'org:manage:lane_profiles',
                            'org:read:lane_profiles',
                            'org:tags:manage',
                            'org:tags:read',
                            'org:sys_memberships:manage',
                            'org:sys_memberships:read',
                          ].some((permission) => has({ permission })) || isAdmin
                    }
                  >
                    <div
                      data-testid="header-divider-lane-performance"
                      className="h-px bg-Border-Default-neutral-01 w-full my-2"
                    ></div>
                  </Protect>
                  {(hasInsightsTemperatureReleasePermission ||
                    hasInsightsLanePerformancePermission) && (
                    <MenuExpand
                      data-testid="insights-menu"
                      icon={
                        <IconReportAnalytics
                          className={`${getMenuIconClass(selected === 'insights', true)}`}
                          stroke={2}
                        />
                      }
                      menu={navLabels.insights}
                      submenu={[
                        ...(hasInsightsLanePerformancePermission
                          ? [
                              {
                                label: navLabels.insightsLanePerformance,
                                link: `/insights/lane-performance/${DASHBOARD_KEYS.EXECUTIVE_SUMMARY}`,
                                icon: (
                                  <IconChartArcs3
                                    className={`text-Icon-strong-06 ${
                                      subMenuName === navLabels.insightsLanePerformance
                                        ? 'text-Misc-brand group-hover:text-Misc-brand'
                                        : ''
                                    }`}
                                    stroke={2}
                                  />
                                ),
                              },
                            ]
                          : []),

                        ...(hasInsightsTemperatureReleasePermission
                          ? [
                              {
                                label: navLabels.insightsTemperatureRelease,
                                link: '/insights/temperature-release',
                                icon: (
                                  <IconProgressCheck
                                    className={`text-Icon-strong-06 ${
                                      subMenuName === navLabels.insightsTemperatureRelease
                                        ? 'text-Misc-brand group-hover:text-Misc-brand'
                                        : ''
                                    }`}
                                    stroke={2}
                                  />
                                ),
                              },
                            ]
                          : []),
                        // {label: "SLA", link: "/insights/sla", icon: <IconHistoryToggle className={`text-Icon-strong-06 ${subMenuName === "SLA" ? "text-Misc-brand group-hover:text-Misc-brand" : ""}`} stroke={2} />}
                      ]}
                      headerExpand={headerExpand}
                      setHeaderExpand={(flag) => setHeaderExpand(flag)}
                      showTooltip={true}
                      type="insights"
                      selected={selected}
                      setSelected={(menu) => setSelected(menu)}
                      setSubmenu={(flag) => setExpandMenu(flag)}
                      subMenuName={subMenuName}
                      setSubMenuName={(name, menu) => {
                        setSubMenuName(name)
                        menu && setMenu(resolveMenuLabel(menu))
                      }}
                      onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                    />
                  )}
                  {hasAutomationPermission && (
                    <MenuExpand
                      data-testid="automation"
                      icon={
                        <IconBolt
                          className={`${getMenuIconClass(selected === 'automation', true)}`}
                          stroke={2}
                        />
                      }
                      menu={navLabels.automation}
                      headerExpand={headerExpand}
                      submenu={[
                        {
                          label: navLabels.workflows,
                          link: '/automation/workflows',
                          permissions: [
                            'org:automation_workflow:manage',
                            'org:automation_workflow:read',
                          ],
                          accessPermissions: checkAutomationWorkflowsViewAccess(uagEnhancements),
                          icon: (
                            <IconPlayerPlay
                              className={`${getSubmenuIconClass(subMenuName === navLabels.workflows)}`}
                              stroke={2}
                            />
                          ),
                        },
                      ]}
                      permissions={[
                        'org:automation_workflow:manage',
                        'org:automation_workflow:read',
                      ]}
                      accessPermissions={checkAutomationWorkflowsViewAccess(uagEnhancements)}
                      setHeaderExpand={(flag) => setHeaderExpand(flag)}
                      showTooltip={true}
                      type="automation"
                      selected={selected}
                      setSelected={(menu) => setSelected(menu)}
                      setSubmenu={(flag) => setExpandMenu(flag)}
                      subMenuName={subMenuName}
                      setSubMenuName={(name, menu) => {
                        setSubMenuName(name)
                        menu && setMenu(resolveMenuLabel(menu))
                      }}
                      onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                    />
                  )}
                  {(hasProductProfilePermission ||
                    hasLaneProfilePermission ||
                    hasTagPermission) && (
                    <MenuExpand
                      data-testid="configuration-menu"
                      icon={
                        <IconSettings
                          className={`${getMenuIconClass(selected === 'configuration', true)}`}
                          stroke={2}
                        />
                      }
                      menu={navLabels.configuration}
                      submenu={[
                        ...(hasProductProfilePermission
                          ? [
                              {
                                label: navLabels.productProfile,
                                link: '/product-profiles',
                                permissions: [
                                  'org:product_profiles:manage',
                                  'org:product_profiles:read',
                                ],
                                accessPermissions: checkUserFeatureAccess('product_profiles', [
                                  'read',
                                  'manage',
                                ]),
                                icon: (
                                  <IconBoxPadding
                                    className={`${getSubmenuIconClass(
                                      subMenuName === navLabels.productProfile,
                                    )}`}
                                    stroke={2}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasLaneProfilePermission
                          ? [
                              {
                                label: navLabels.laneProfile,
                                link: '/lane-profiles',
                                permissions: ['org:manage:lane_profiles', 'org:read:lane_profiles'],
                                accessPermissions: checkUserFeatureAccess('lane_profiles', [
                                  'read',
                                  'manage',
                                ]),
                                icon: (
                                  <IconRoute
                                    className={`${getSubmenuIconClass(
                                      subMenuName === navLabels.laneProfile,
                                    )}`}
                                    stroke={2}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(hasTagPermission
                          ? [
                              {
                                label: navLabels.tags,
                                link: '/tags',
                                permissions: ['org:tags:manage', 'org:tags:read'],
                                accessPermissions: checkUserFeatureAccess('tags', [
                                  'read',
                                  'manage',
                                ]),
                                icon: (
                                  <IconTag
                                    className={`${getSubmenuIconClass(subMenuName === navLabels.tags)}`}
                                    stroke={2}
                                  />
                                ),
                              },
                            ]
                          : []),
                      ]}
                      permissions={
                        hasProductProfilePermission || hasLaneProfilePermission || hasTagPermission
                          ? [
                              'org:product_profiles:manage',
                              'org:product_profiles:read',
                              'org:manage:lane_profiles',
                              'org:read:lane_profiles',
                              'org:tags:manage',
                              'org:tags:read',
                            ]
                          : []
                      }
                      accessPermissions={
                        checkUserFeatureAccess('product_profiles', ['read', 'manage']) ||
                        checkUserFeatureAccess('lane_profiles', ['read', 'manage']) ||
                        checkUserFeatureAccess('tags', ['read', 'manage'])
                      }
                      headerExpand={headerExpand}
                      setHeaderExpand={(flag) => setHeaderExpand(flag)}
                      showTooltip={true}
                      type="configuration"
                      selected={selected}
                      setSelected={(menu) => setSelected(menu)}
                      setSubmenu={(flag) => setExpandMenu(flag)}
                      subMenuName={subMenuName}
                      setSubMenuName={(name, menu) => {
                        setSubMenuName(name)
                        menu && setMenu(resolveMenuLabel(menu))
                      }}
                      onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                    />
                  )}
                  {hasOldManagePermission && (
                    <MenuExpand
                      data-testid="manage-menu"
                      icon={
                        <IconSettings2
                          className={`${getMenuIconClass(selected === 'manage', true)}`}
                          stroke={2}
                        />
                      }
                      menu={navLabels.manage}
                      headerExpand={headerExpand}
                      submenu={[
                        ...(haveOrganizationSettingsPermission
                          ? [
                              {
                                label: navLabels.organization,
                                link: '/manage/organization',
                                permissions: [
                                  'org:sys_memberships:manage',
                                  'org:sys_memberships:read',
                                ],
                                icon: (
                                  <IconBuildingSkyscraper
                                    className={`${getSubmenuIconClass(subMenuName === navLabels.organization)}`}
                                    stroke={2}
                                  />
                                ),
                              },
                            ]
                          : []),
                        {
                          label: navLabels.userGroup,
                          link: '/user-access-groups',
                          permissions: [
                            'org:read:user_access_groups',
                            'org:manage:user_access_groups',
                          ],
                          icon: (
                            <IconUsersGroup
                              className={`${getSubmenuIconClass(subMenuName === navLabels.userGroup)}`}
                              stroke={2}
                            />
                          ),
                        },
                      ]}
                      permissions={['org:sys_memberships:manage', 'org:sys_memberships:read']}
                      setHeaderExpand={(flag) => setHeaderExpand(flag)}
                      showTooltip={true}
                      type="manage"
                      selected={selected}
                      setSelected={(menu) => setSelected(menu)}
                      setSubmenu={(flag) => setExpandMenu(flag)}
                      subMenuName={subMenuName}
                      setSubMenuName={(name, menu) => {
                        setSubMenuName(name)
                        menu && setMenu(resolveMenuLabel(menu))
                      }}
                      onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                    />
                  )}
                  {haveAdminSettingsPermission && (
                    <>
                      <div
                        data-testid="divider-admin-settings"
                        className="h-px bg-Border-Default-neutral-01 w-full my-2"
                      ></div>
                      <MenuExpand
                        data-testid="admin-settings-menu"
                        icon={
                          <IconAdjustmentsCog
                            className={`${getMenuIconClass(selected === 'app_config', true)}`}
                            stroke={2}
                          />
                        }
                        menu={navLabels.adminSettings}
                        headerExpand={headerExpand}
                        submenu={getAdminSettingsSubmenu()}
                        setHeaderExpand={(flag) => setHeaderExpand(flag)}
                        showTooltip={true}
                        type="app_config"
                        selected={selected}
                        setSelected={(menu) => setSelected(menu)}
                        setSubmenu={(flag) => setExpandMenu(flag)}
                        subMenuName={subMenuName}
                        setSubMenuName={(name, menu) => {
                          setSubMenuName(name)
                          menu && setMenu(resolveMenuLabel(menu))
                        }}
                        onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                      />
                    </>
                  )}
                  {haveHelpPagePermission && (
                    <>
                      <div
                        data-testid="header-divider-help"
                        className="h-px bg-Border-Default-neutral-01 w-full my-2"
                      ></div>
                      <MenuExpand
                        data-testid="help-menu"
                        icon={
                          <IconHelp
                            className={`${getMenuIconClass(selected === 'help', false)}`}
                            stroke={2}
                          />
                        }
                        menu={navLabels.help}
                        headerExpand={headerExpand}
                        setHeaderExpand={(flag) => setHeaderExpand(flag)}
                        showTooltip={true}
                        type="help"
                        selected={selected}
                        setSelected={(menu) => setSelected(menu)}
                        setSubmenu={(flag) => setExpandMenu(flag)}
                        subMenuName={subMenuName}
                        setSubMenuName={(name, menu) => {
                          setSubMenuName(name)
                          menu && setMenu(resolveMenuLabel(menu))
                        }}
                        link="/help"
                        onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                      />
                    </>
                  )}
                  {hasOrganizationManagementPermission && (
                    <>
                      <div
                        data-testid="header-divider-tenant-settings"
                        className="h-px bg-Border-Default-neutral-01 w-full my-2"
                      ></div>
                      <MenuExpand
                        data-testid="tenant-settings-menu"
                        icon={
                          <IconBuildingSkyscraper
                            className={`${getMenuIconClass(selected === 'tenant-org-management', false)}`}
                            stroke={2}
                          />
                        }
                        menu={navLabels.manageOrganizations}
                        headerExpand={headerExpand}
                        setHeaderExpand={(flag) => setHeaderExpand(flag)}
                        showTooltip={true}
                        type="tenant-org-management"
                        selected={selected}
                        setSelected={(menu) => setSelected(menu)}
                        setSubmenu={(flag) => setExpandMenu(flag)}
                        subMenuName={subMenuName}
                        setSubMenuName={(name, menu) => {
                          setSubMenuName(name)
                          menu && setMenu(resolveMenuLabel(menu))
                        }}
                        link="/tenant/org-management"
                        onDiscardUnsavedChanges={onDiscardUnsavedChanges}
                      />
                    </>
                  )}
                </div>
              </SmartScroll>
            </div>
            <div className="shrink-0 px-3 w-full flex flex-col">
              {(uagEnhancements ? false : bottomPoweredByPaxafe) && headerExpand ? (
                <div
                  data-testid="header-powered-by-paxafe-bottom-expanded"
                  className="flex items-center mt-2 gap-1.5"
                >
                  <span className="text-Text-small-04 text-xs ml-2">Powered by</span>
                  <img src={PaxafeLogo} alt="Paxafe Logo" className="h-[11px] inline-block" />
                </div>
              ) : (uagEnhancements ? false : bottomPoweredByPaxafe) && !headerExpand ? (
                <div
                  data-testid="header-powered-by-paxafe-bottom-collapsed"
                  className="flex flex-col items-center mt-2 gap-1.5"
                >
                  <span className="text-Text-small-04 text-[8px] text-center gap-1">
                    Powered by
                  </span>
                  <img src={XLogo} alt="Paxafe Logo" className="h-[16px] inline-block" />
                </div>
              ) : null}
              <div
                data-testid="header-divider-footer"
                className="h-px bg-black-100 w-full mb-2"
              ></div>
              <div className={`cc-view mb-2 ${headerExpand ? 'ml-1' : ''}`}>
                <UserMenu
                  userImpersonationOrgs={haveUserImpersonationPermission}
                  headerExpand={headerExpand}
                />
              </div>
            </div>
          </div>
        </div>
        {headerExpand ? (
          <div
            data-testid="header-sidebar-overlay"
            className="absolute top-0 right-0 bottom-0 left-[255px] z-40 bg-BG-Overlay-default"
            onClick={(_e) => {
              setHeaderExpand(false)
              updateHeaderStates()
            }}
          ></div>
        ) : (
          <></>
        )}
        {showVersionAlert?.version && !session?.actor && (
          <div className="fixed bottom-[10px] left-[80px] z-50 rounded-md border border-Border-Default-neutral-01 p-4 shadow-sm bg-BG-Default-white slide-in-left">
            <IconX
              className="w-4 h-4 text-Icon-strong-06 cursor-pointer ml-1 absolute top-2 right-2"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setShowVersionAlert((prev) => ({ ...prev, version: false }))
              }}
            />
            <div className="flex items-center gap-1 text-md font-semibold text-Text-medium-06 mb-1 mr-[10px]">
              <IconInfoCircle className="w-4 h-4 text-Icon-strong-06" />
              New release version available
            </div>
            <div className="flex items-center gap-1 text-sm text-Text-medium-06 mb-4 pl-[20px]">
              Refresh to see the latest update
            </div>
            <div className="flex justify-between">
              <Button
                type="primary"
                className="text-xs font-medium px-4 py-2 ml-[20px]"
                onClick={async (e) => {
                  e.stopPropagation()
                  if (hasPendingUnsavedNavigation()) {
                    setShowAppAlert((prev) => ({ ...prev, show: true, path: location.pathname }))
                    return false
                  } else {
                    setShowAppAlert((prev) => ({ ...prev, show: false, path: '' }))
                  }
                  // Ask router to move this session to latest before reload.
                  try {
                    await fetch('/router/upgrade', {
                      method: 'POST',
                      credentials: 'same-origin',
                    })
                  } catch (error) {
                    console.warn('Failed to upgrade router cookie before refresh', error)
                  }
                  window.location.reload()
                }}
                text="Refresh"
              />
              {showVersionAlert?.releaseNotes && (
                <Button
                  type="primary"
                  className="text-xs font-medium px-4 py-2 ml-[20px]"
                  onClick={(e) => {
                    e.stopPropagation()
                    window.open(showVersionAlert?.releaseNotes, '_blank')
                  }}
                  text="Release Notes"
                />
              )}
            </div>
          </div>
        )}
      </SignedIn>
      {/* {isSearchPopupActive ? (
        <div
          data-testid="header-search-popup-overlay"
          className="pointer-events-none absolute inset-0 z-[55] bg-BG-Overlay-default"
          aria-hidden="true"
        />
      ) : null} */}
      <div
        data-testid="header-content-container"
        className={`flex flex-col items-start flex-grow h-full overflow-hidden ${
          position?.includes('relative') ? 'bg-transparent' : ''
        } ${isSearchPopupActive ? 'relative z-[60]' : ''}`}
      >
        <SignedIn>
          <HeaderBar
            menu={menu}
            menuNavigation={menuNavigation}
            subMenuNavigation={subMenuNavigation}
            subMenu={subMenuName}
            feature={featureName}
            headerExpand={headerExpand}
            setHeaderExpand={(flag) => setHeaderExpand(flag)}
            hideBreadcrumb={hideBreadcrumb}
            rightComponent={rightComponent}
            hideTimezone={hideTimezone}
            leftComponent={noPermissions ? null : leftComponent}
            transparentBackground={position?.includes('relative')}
            searchPopupProp={position?.includes('search-popup')}
            disableEvents={position?.includes('no-events')}
            darkThemeHeaderBar={darkThemeHeaderBar}
            user={user}
            onDiscardUnsavedChanges={onDiscardUnsavedChanges}
          />
        </SignedIn>
        <div
          data-testid="header-children-container"
          className={`w-full h-[calc(100vh-64px)] ${
            position?.includes('relative') ? 'relative bg-transparent' : 'overflow-y-auto'
          }`}
        >
          {orgSwitchActive ? (
            <div className="w-full h-full flex items-center justify-center bg-BG-Default-neutral-00">
              <Loader message="Switching organization…" data-testid="header-org-switch-loader" />
            </div>
          ) : noPermissions && location.pathname !== '/landing-page' ? (
            <NoPermissionEmptyState
              uagEnhancements={uagEnhancements}
              featurePermissions={featurePermissions}
              userFeaturePermissions={userFeaturePermissions}
              onSignOut={customSignOut}
              containerTestId="header-no-permissions-empty-state"
              noDataFoundTestId="header-no-data-found"
            />
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  )
}

export default Header
